/**
 * useAgentQASources Hook
 * Manages Q&A source attachments for agents.
 */

import { useCallback, useState } from 'react';
import adminApi from '@/services/admin.api';
import type { QASourceItem } from '@/hooks/useQASources';

interface UseAgentQASourcesResult {
  qas: QASourceItem[];
  loading: boolean;
  error: string;
  loadQAs: (agentId: string) => Promise<void>;
  detachQA: (agentId: string, qaId: string) => Promise<void>;
}

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.error?.message) return error.response.data.error.message;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message) return error.message;
  return 'An unexpected error occurred';
};

export const useAgentQASources = (): UseAgentQASourcesResult => {
  const [qas, setQAs] = useState<QASourceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadQAs = useCallback(async (agentId: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getAgentQASources(agentId);
      setQAs(data);
    } catch (err: any) {
      setError(extractErrorMessage(err));
      setQAs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const detachQA = useCallback(async (agentId: string, qaId: string) => {
    try {
      setError('');
      await adminApi.detachQASourceFromAgent(agentId, qaId);
      setQAs((prev) => prev.filter((q) => q._id !== qaId));
    } catch (err: any) {
      throw new Error(extractErrorMessage(err));
    }
  }, []);

  return {
    qas,
    loading,
    error,
    loadQAs,
    detachQA,
  };
};
